import { useMemo } from "react";
import type { QrCenterType, QrCornerStyle, QrDotStyle } from "../types";
import { hasCenterArt } from "../utils/qr-density";
import { buildQrGeometry } from "../utils/qr-geometry";
import { buildQrMatrix } from "../utils/qr-matrix";

const isTransparent = (color?: string) =>
  !color || color === "transparent" || color === "none";

type Props = {
  value: string;
  color: string;
  backgroundColor: string;
  dotStyle: QrDotStyle;
  cornerStyle: QrCornerStyle;
  centerType: QrCenterType;
  centerImage?: string;
  centerIcon?: string;
  /** Percent of the code's width. */
  centerSize?: string;
};

/**
 * The code itself: an SVG drawn from the module matrix, plus an HTML overlay
 * in the cleared middle for a logo / image / icon. Colours go through `style`
 * so theme tokens (`var(--theme-color-1)`) resolve like everywhere else.
 */
export const QrCodeSvg = ({
  value,
  color,
  backgroundColor,
  dotStyle,
  cornerStyle,
  centerType,
  centerImage,
  centerIcon,
  centerSize,
}: Props) => {
  const hasCenter = hasCenterArt({ centerType, centerImage, centerIcon });

  const geometry = useMemo(() => {
    // A logo hides part of the code, so it needs the highest recovery level.
    const matrix = buildQrMatrix(value, hasCenter ? "H" : "M");
    if (!matrix) return null;
    return buildQrGeometry(matrix, {
      dotStyle,
      cornerStyle,
      withCenter: hasCenter,
      centerRatio: Number(centerSize) / 100,
    });
  }, [value, hasCenter, dotStyle, cornerStyle, centerSize]);

  if (!geometry) return null;

  const { viewSize, dots, eyeFrames, eyeCenters, hole } = geometry;
  const fill = color || "#000000";
  const background = isTransparent(backgroundColor)
    ? undefined
    : backgroundColor;

  return (
    <>
      <svg
        viewBox={`0 0 ${viewSize} ${viewSize}`}
        width="100%"
        height="100%"
        shapeRendering={dotStyle === "square" ? "crispEdges" : undefined}
        role="img"
        aria-label={`QR code: ${value}`}
        className="tw-block"
      >
        {background && (
          <rect
            width={viewSize}
            height={viewSize}
            style={{ fill: background }}
          />
        )}
        <path d={dots} style={{ fill }} />
        <path d={eyeFrames} fillRule="evenodd" style={{ fill }} />
        <path d={eyeCenters} style={{ fill }} />
      </svg>
      {hole && (
        <div
          className="tw-absolute tw-flex tw-items-center tw-justify-center tw-overflow-hidden"
          style={{
            left: `${((hole.start + (hole.size - hole.art) / 2) / viewSize) * 100}%`,
            top: `${((hole.start + (hole.size - hole.art) / 2) / viewSize) * 100}%`,
            width: `${(hole.art / viewSize) * 100}%`,
            height: `${(hole.art / viewSize) * 100}%`,
          }}
        >
          {centerType === "icon" ? (
            <CenterIcon
              icon={centerIcon as string}
              color={fill}
              fontSize={`${(hole.art / viewSize) * 85}cqw`}
            />
          ) : (
            <img
              src={centerImage}
              alt=""
              draggable={false}
              className="tw-block tw-h-full tw-w-full tw-object-contain"
            />
          )}
        </div>
      )}
    </>
  );
};

const CenterIcon = ({
  icon,
  color,
  fontSize,
}: {
  icon: string;
  color: string;
  fontSize: string;
}) => {
  const style = { color, fontSize, lineHeight: 1 };
  if (icon.startsWith("ri-")) return <i className={icon} style={style} />;
  if (icon.startsWith("fa-"))
    return <i className={`fa-solid ${icon}`} style={style} />;
  return (
    <span className="material-icons" style={style}>
      {icon}
    </span>
  );
};
